import type { LineItemDraft, QuoteLineItemInput } from "@/api/types/quote";

/**
 * Client-side quote math — PREVIEW ONLY.
 *
 * The server is the source of truth for every cent stored on a quote (house
 * rule: the frontend never computes a price the API stores). This module
 * exists solely to render a live "here's roughly what this will total"
 * preview in the builder before save, and to turn the editable dollar-input
 * rows into the integer-cents payload the API expects. After a successful
 * POST/PATCH, the UI must switch to displaying the server's returned cents.
 */

/** Parse a dollar-string input ("1,350", "1350.5", "") into whole cents. */
export function dollarsToCents(input: string): number {
  const cleaned = input.replace(/[^0-9.-]/g, "");
  if (cleaned.trim() === "") return 0;
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 100);
}

export function draftToLineItemInput(draft: LineItemDraft): QuoteLineItemInput {
  const qty = Number(draft.qty) || 0;
  const unit_cents = dollarsToCents(draft.unitDollars);
  return {
    label: draft.label.trim(),
    qty,
    unit_cents,
    amount_cents: Math.round(qty * unit_cents),
  };
}

export interface QuotePreviewTotals {
  subtotal_cents: number;
  service_charge_cents: number;
  total_cents: number;
  deposit_cents: number;
}

/**
 * Mirrors the server's expected formula (playbook example: $1,350 subtotal,
 * 18% service charge -> $1,593.00 total, 25% deposit -> $398.25 deposit due
 * — deposit is a percentage of the total, not the bare subtotal). Rounds to
 * the nearest cent at each step, same as money must be rounded server-side.
 */
export function computeQuotePreview(
  lineItems: QuoteLineItemInput[],
  serviceChargePct: number,
  depositPct: number,
): QuotePreviewTotals {
  const subtotal_cents = lineItems.reduce((sum, li) => sum + li.amount_cents, 0);
  const service_charge_cents = Math.round(subtotal_cents * (serviceChargePct / 100));
  const total_cents = subtotal_cents + service_charge_cents;
  const deposit_cents = Math.round(total_cents * (depositPct / 100));
  return { subtotal_cents, service_charge_cents, total_cents, deposit_cents };
}
